import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonBase, Grid, TableCell, TableRow, Skeleton } from '@mui/material';
import { ContainedButton } from 'components/ContainedButton';
import { Text } from 'components/text';
import { TextWithIcon } from 'components/text-with-icon';
import { useUserInformationContext } from 'contexts/userContext';
import { TFunction } from 'i18next';
import React, { FC, useState } from 'react';
import { TransferBlockchainData } from 'views/orders/payments/types';

import { rowBorderColor, statusSigners, wireTransferStatusIcons } from '../../utils';
import { InstructionsRow } from './components/InstructionsRow';

type RowProps = {
  t: TFunction;
  transfer: TransferBlockchainData;
  onClick: () => void;
  onViewChainClick: () => void;
};

export const Row: FC<RowProps> = ({ t, transfer, onClick, onViewChainClick }) => {
  const [open, setOpen] = useState(false);
  const { currentUser } = useUserInformationContext();

  const signers = transfer.document?.authorization.map((mail) => {
    const foundSignature =
      (transfer.document?.signatures &&
        transfer.document?.signatures.find((signature) => signature.email === mail)) ||
      undefined;
    const status = foundSignature ? foundSignature.action : 'pending';

    return {
      mail,
      status,
    };
  });

  const amISigner = signers?.find((signer) => signer.mail === currentUser?.username);
  const isRejected = signers?.find((signer) => signer.status === 'block-order');
  const borderColor =
    transfer.document?.status &&
    rowBorderColor[transfer.document?.status.toLowerCase().replace(' ', '')];

  const renderFilename = () => {
    const filename = transfer.document?.submission?.filename;
    if (filename) {
      return <TextWithIcon value={filename} iconColor="#90a4ae" icon="file" />;
    }

    return filename;
  };

  const renderStatus = () => {
    if (amISigner && amISigner.status === 'pending' && !isRejected) {
      return <ContainedButton text={t('payments:authorize')} onClick={onClick} />;
    }

    const status = transfer.document?.status.toLowerCase() ?? '';
    if (wireTransferStatusIcons[status]) {
      return (
        <TextWithIcon
          value={transfer.document?.status ?? ''}
          color="primary"
          icon={wireTransferStatusIcons[status].icon}
          iconColor={wireTransferStatusIcons[status].color}
          iconSize="1x"
        />
      );
    }

    return <Text value={transfer.document?.status ?? ''} color="primary" />;
  };

  const { isDataLoading } = transfer;

  return (
    <>
      <TableRow hover key={transfer.reference}>
        <TableCell style={{ borderLeft: `6px solid ${borderColor}` }}>
          <ButtonBase onClick={() => setOpen(!open)} style={{ gap: '5px' }}>
            {open ? <FontAwesomeIcon icon="angle-up" /> : <FontAwesomeIcon icon="angle-down" />}
            <TableCell>{transfer.reference}</TableCell>
          </ButtonBase>
        </TableCell>
        <TableCell>
          {isDataLoading ? (
            <Skeleton height={40} />
          ) : (
            <>
              <Text value={transfer.amount || 0} isMoney color="primary" />
            </>
          )}
        </TableCell>
        <TableCell>
          {isDataLoading ? <Skeleton height={40} /> : <>{transfer.date || null}</>}
        </TableCell>
        <TableCell>
          {isDataLoading ? <Skeleton height={40} width={200} /> : <>{renderFilename()}</>}
        </TableCell>
        <TableCell>
          {isDataLoading ? (
            <Skeleton height={40} width={200} />
          ) : (
            <Grid container direction="column" spacing={1}>
              {signers?.map((signer) => (
                <Grid item>
                  <TextWithIcon
                    value={signer.mail}
                    icon={statusSigners[signer.status].icon}
                    iconColor={statusSigners[signer.status].color}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </TableCell>
        <TableCell>{isDataLoading ? <Skeleton height={40} /> : <>{renderStatus()}</>}</TableCell>
        <TableCell>
          {isDataLoading ? (
            <Skeleton height={40} />
          ) : (
            <>
              <ContainedButton text={t('orders:viewChainButton')} onClick={onViewChainClick} />
            </>
          )}
        </TableCell>
      </TableRow>
      <InstructionsRow t={t} open={open} instructions={transfer.document?.instructions ?? []} />
    </>
  );
};
